"use client";

import {
  Target,
  Repeat,
  TrendingUp,
  Briefcase,
  PresentationIcon,
  Handshake,
  Sparkles,
  MessageSquare,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Zap
} from "lucide-react";
import { useRouter } from "next/navigation";
import { LandingHeader } from "@/components/landing-header";
import TeamSection from "@/components/team-section";
import { TestimonialCarousel } from "@/components/testimonial-carousel";

export default function Page() {
  const router = useRouter();

  return (
    <>
      <LandingHeader />
      <main className="min-h-screen" data-lk-theme="default">
        {/* Hero Section - Cluely Theme */}
        <section id="hero" className="relative w-full text-center flex flex-col items-center justify-center min-h-screen px-4 py-20 overflow-hidden bg-[#0a0a0f]">
          {/* Animated background elements - Blue theme */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
          </div>

          <div className="mb-12 relative z-10 max-w-5xl mx-auto w-full">
            <div className="flex justify-center mb-6">
              <div className="inline-block px-4 py-2 bg-cyan-500/10 rounded-full border border-cyan-500/20 backdrop-blur-sm">
                <span className="text-cyan-300 text-sm font-medium flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  AI-Powered Practice Platform
                  <span className="ml-1 px-2 py-0.5 bg-cyan-500/20 text-cyan-200 text-xs font-bold rounded-md border border-cyan-400/30">
                    BETA
                  </span>
                </span>
              </div>
            </div>

            <h1 className="font-[family-name:var(--font-playfair)] text-5xl md:text-6xl lg:text-8xl font-bold leading-tight tracking-tight text-white mb-6 text-center">
              Your AI{" "}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-cyan-400 via-blue-400 to-sky-400 bg-clip-text text-transparent">
                  practice partner
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 opacity-20 blur-xl"></span>
              </span>
              <br />
              for high-stakes moments
            </h1>
            <p className="text-xl md:text-2xl mt-8 text-gray-400 max-w-3xl mx-auto font-light leading-relaxed">
              Run roleplay scenarios before your pitch, board meeting, or negotiation—so you walk in{" "}
              <span className="text-cyan-400 font-semibold">prepared</span>,{" "}
              <span className="text-blue-400 font-semibold">confident</span>, and{" "}
              <span className="text-sky-400 font-semibold">ready to win</span>.
            </p>

            {/* CTA Button - Cluely Theme */}
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => router.push('/setup')}
                className="group relative bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold px-10 py-5 rounded-xl text-lg transition-all duration-300 shadow-2xl shadow-cyan-500/20 hover:shadow-cyan-500/40 transform hover:scale-105 hover:-translate-y-1"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Start Practicing Now - Free
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-xl blur opacity-50 group-hover:opacity-75 transition-opacity"></div>
              </button>

              <p className="text-sm text-gray-500 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                No credit card required
              </p>
            </div>
          </div>

          {/* Demo Screenshot - Cluely Style */}
          <div className="relative z-10 max-w-6xl mx-auto w-full mt-16 px-4">
            <div className="relative">
              {/* Glow effect behind image - Blue theme */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-sky-500/20 blur-3xl"></div>

              {/* Main demo image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-cyan-500/10 border border-cyan-500/20">
                <img
                  src="/images/image.png"
                  alt="AI Practice Partner Demo"
                  className="w-full h-auto"
                />

                {/* Annotation pointing to AI Agent button */}
                <div className="absolute bottom-[8%] right-[35%] transform translate-x-1/2">
                  {/* Pulsing pointer */}
                  <div className="relative">
                    <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 animate-bounce">
                      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-2 rounded-lg shadow-xl border border-cyan-400/50 whitespace-nowrap">
                        <p className="text-sm font-bold flex items-center gap-2">
                          👆 Click &quot;AI Agent&quot; to invite Jenna!
                        </p>
                      </div>
                      {/* Arrow pointing down */}
                      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-cyan-500"></div>
                    </div>
                    {/* Pulsing circle on the button */}
                    <div className="w-16 h-16 bg-cyan-500/30 rounded-full animate-ping"></div>
                    <div className="absolute inset-0 w-16 h-16 bg-cyan-500/50 rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-3 rounded-full shadow-xl border border-cyan-400/30">
                <p className="text-white font-semibold text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Practice with AI-powered Jenna
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Feature Cards Section - Cluely Theme */}
        <section id="features" className="relative bg-[#0f0f14] py-24">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-20">
              <div className="inline-block mb-4 px-4 py-2 bg-cyan-500/10 rounded-full border border-cyan-500/20">
                <span className="text-cyan-400 text-sm font-semibold">WHY CHOOSE US</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-white via-cyan-200 to-white bg-clip-text text-transparent mb-6">
                Practice Like Never Before
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto font-semibold">
                Unlike static training materials, our AI adapts to challenge you with realistic scenarios
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="group relative text-center p-10 rounded-3xl bg-[#1a1a1f] border-2 border-cyan-500/20 hover:border-cyan-400 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/20 hover:-translate-y-2">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform">
                    <Target className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Unlimited Practice</h3>
                  <p className="text-gray-400 text-lg leading-relaxed">No scheduling, no judgment. Practice anytime, as many times as you need.</p>
                </div>
              </div>

              <div className="group relative text-center p-10 rounded-3xl bg-[#1a1a1f] border-2 border-blue-500/20 hover:border-blue-400 transition-all duration-300 hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-2">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-sky-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-sky-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform">
                    <Repeat className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Dynamic Scenarios</h3>
                  <p className="text-gray-400 text-lg leading-relaxed">Different objections every time. Never predictable, always challenging.</p>
                </div>
              </div>

              <div className="group relative text-center p-10 rounded-3xl bg-[#1a1a1f] border-2 border-cyan-500/20 hover:border-cyan-400 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/20 hover:-translate-y-2">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform">
                    <TrendingUp className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Real Pressure</h3>
                  <p className="text-gray-400 text-lg leading-relaxed">Experience authentic pushback and tough questions before the real moment.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section - Cluely Theme */}
        <section id="how-it-works" className="relative bg-[#0a0a0f] py-24 overflow-hidden border-t border-cyan-500/10">
          {/* Animated background */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4">
            <div className="text-center mb-20">
              <div className="inline-block mb-4 px-4 py-2 bg-purple-500/20 rounded-full border border-purple-500/30">
                <span className="text-purple-300 text-sm font-semibold">SIMPLE PROCESS</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
                How It Works
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Three simple steps to transform your high-stakes performance
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 relative">
              {/* Connecting line */}
              <div className="hidden md:block absolute top-16 left-1/4 right-1/4 h-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-sky-500 opacity-30"></div>

              <div className="relative text-center group">
                <div className="relative inline-block mb-8">
                  <div className="w-24 h-24 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-cyan-500/50 group-hover:scale-110 transition-transform duration-300 rotate-3 group-hover:rotate-6">
                    <Sparkles className="w-12 h-12 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                    1
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Choose Your Scenario</h3>
                <p className="text-gray-300 text-lg leading-relaxed">Select from investor pitch, board meeting, or negotiation scenarios</p>
              </div>

              <div className="relative text-center group">
                <div className="relative inline-block mb-8">
                  <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-green-500/50 group-hover:scale-110 transition-transform duration-300 -rotate-3 group-hover:-rotate-6">
                    <MessageSquare className="w-12 h-12 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                    2
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Describe Your Situation</h3>
                <p className="text-gray-300 text-lg leading-relaxed">Tell Jenna about your product, deal, or presentation context</p>
              </div>

              <div className="relative text-center group">
                <div className="relative inline-block mb-8">
                  <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-orange-500/50 group-hover:scale-110 transition-transform duration-300 rotate-3 group-hover:rotate-6">
                    <Zap className="w-12 h-12 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                    3
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Practice & Improve</h3>
                <p className="text-gray-300 text-lg leading-relaxed">She adapts and challenges you with realistic objections and questions</p>
              </div>
            </div>
          </div>
        </section>

        {/* Scenarios Section - Cluely Theme */}
        <section id="scenarios" className="relative bg-[#0f0f14] py-24 border-t border-cyan-500/10">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-20">
              <div className="inline-block mb-4 px-4 py-2 bg-cyan-500/10 rounded-full border border-cyan-500/20">
                <span className="text-cyan-400 text-sm font-semibold">PRACTICE SCENARIOS</span>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-white via-cyan-200 to-white bg-clip-text text-transparent mb-6">
                Master Every High-Stakes Moment
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Practice the scenarios that matter most to your success
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="group relative bg-[#1a1a1f] p-10 rounded-3xl shadow-xl hover:shadow-2xl hover:shadow-cyan-500/20 transition-all duration-300 hover:-translate-y-2 cursor-pointer border-2 border-cyan-500/20 hover:border-cyan-400 overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative">
                  <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <Briefcase className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4 text-center">Investor Pitches</h3>
                  <p className="text-gray-400 mb-6 text-lg leading-relaxed">Face skeptical VCs and strategic partners who question your market size, unit economics, and competitive differentiation.</p>
                  <ul className="text-sm text-gray-500 space-y-2">
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2 mt-0.5">•</span>
                      <span>&quot;Your projections seem unrealistic&quot;</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2 mt-0.5">•</span>
                      <span>&quot;What&apos;s your customer acquisition cost?&quot;</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-500 mr-2 mt-0.5">•</span>
                      <span>&quot;How are you different from competitors?&quot;</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-[#1a1a1f] p-8 rounded-2xl shadow-lg hover:shadow-2xl hover:shadow-blue-500/20 transition-all hover:-translate-y-1 cursor-pointer border-2 border-blue-500/20 hover:border-blue-400">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                  <PresentationIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4 text-center">Board Presentations</h3>
                <p className="text-gray-600 mb-6">Present to demanding board members who focus on performance, ROI, and strategic direction.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-0.5">•</span>
                    <span>&quot;These numbers don&apos;t match projections&quot;</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-0.5">•</span>
                    <span>&quot;What&apos;s the timeline to profitability?&quot;</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-500 mr-2 mt-0.5">•</span>
                    <span>&quot;How does this affect shareholder value?&quot;</span>
                  </li>
                </ul>
              </div>

              <div className="bg-[#1a1a1f] p-8 rounded-2xl shadow-lg hover:shadow-2xl hover:shadow-sky-500/20 transition-all hover:-translate-y-1 cursor-pointer border-2 border-sky-500/20 hover:border-sky-400">
                <div className="w-16 h-16 bg-gradient-to-r from-sky-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                  <Handshake className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4 text-center">Negotiations</h3>
                <p className="text-gray-600 mb-6">Navigate tough negotiators who push for better terms, pricing, and contract conditions.</p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 mt-0.5">•</span>
                    <span>&quot;Your price is higher than competitors&quot;</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 mt-0.5">•</span>
                    <span>&quot;We need more flexibility on terms&quot;</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-500 mr-2 mt-0.5">•</span>
                    <span>&quot;What&apos;s your lowest price?&quot;</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Before/After Section */}
        <section className="bg-gradient-to-r from-gray-900 to-black py-20">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                The Difference Practice Makes
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-gray-800/50 p-8 rounded-2xl border border-red-900/30">
                <h3 className="text-2xl font-semibold text-red-400 mb-8 text-center">Without Practice</h3>
                <div className="space-y-5">
                  <div className="flex items-start text-gray-300">
                    <XCircle className="w-6 h-6 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-lg">Wing it and hope for the best</span>
                  </div>
                  <div className="flex items-start text-gray-300">
                    <XCircle className="w-6 h-6 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-lg">Get caught off guard by objections</span>
                  </div>
                  <div className="flex items-start text-gray-300">
                    <XCircle className="w-6 h-6 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-lg">Stumble through tough questions</span>
                  </div>
                  <div className="flex items-start text-gray-300">
                    <XCircle className="w-6 h-6 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-lg">Lose deals to better-prepared competitors</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-800/50 p-8 rounded-2xl border border-green-900/30">
                <h3 className="text-2xl font-semibold text-green-400 mb-8 text-center">With Practice</h3>
                <div className="space-y-5">
                  <div className="flex items-start text-gray-300">
                    <CheckCircle2 className="w-6 h-6 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-lg">Walk in prepared and confident</span>
                  </div>
                  <div className="flex items-start text-gray-300">
                    <CheckCircle2 className="w-6 h-6 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-lg">Handle any objection smoothly</span>
                  </div>
                  <div className="flex items-start text-gray-300">
                    <CheckCircle2 className="w-6 h-6 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-lg">Turn tough questions into opportunities</span>
                  </div>
                  <div className="flex items-start text-gray-300">
                    <CheckCircle2 className="w-6 h-6 text-green-400 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-lg">Close deals with confidence</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <TestimonialCarousel />

        {/* Team Section */}
        <TeamSection />

        {/* Final CTA Section - Cluely Theme */}
        <section className="relative bg-[#0a0a0f] py-24 overflow-hidden border-t border-cyan-500/10">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          </div>

          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Performance?
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
              Practice with AI to win your biggest moments
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => router.push('/setup')}
                className="group relative bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold px-10 py-5 rounded-xl text-lg transition-all duration-300 shadow-2xl shadow-cyan-500/20 hover:shadow-cyan-500/40 transform hover:scale-105"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Start Practicing Now - Free
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-xl blur opacity-50 group-hover:opacity-75 transition-opacity"></div>
              </button>
              <button
                className="border-2 border-cyan-500/30 text-gray-400 font-semibold px-8 py-4 rounded-xl text-lg cursor-not-allowed relative group"
                disabled
              >
                <span>Custom Setup</span>
                <span className="ml-2 text-sm bg-cyan-500/10 px-2 py-1 rounded border border-cyan-500/20">Coming Soon</span>
              </button>
            </div>

            <p className="text-sm text-gray-500 mt-6 flex items-center justify-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-cyan-400" />
              No credit card required • Start in 30 seconds
            </p>
          </div>
        </section>
      </main>
    </>
  );
}
